import { type QueryKey, type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import type { QuoteAPI } from 'receivable-invoices-js/react/api/models/quote';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

interface Params {
  clientId: string;
}

interface QueryResponse {
  data: QuoteAPI[];
  // Can be added if we need pagination
  // meta: {};
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/quotes/shared`;

export function fetchQuotesKey(clientId: string): QueryKey {
  return ['quotes', clientId] as QueryKey;
}

export async function fetchQuotes(params: Params, customOptions: CustomOptions) {
  const { onError } = customOptions;

  try {
    const response = await qontoClient<QueryResponse>(URL, {
      params: {
        client_id: params.clientId,
        page: {
          number: 1,
          size: 100,
        },
      },
    });

    const normalizedResponse = response.data.map(quote => ({
      ...quote.attributes,
      id: quote.id,
    }));

    return camelizeKeys(normalizedResponse);
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

export type FetchQuotesResponse = Awaited<ReturnType<typeof fetchQuotes>>;

export function useFetchQuotes(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchQuotesResponse>>
) {
  return useQuery({
    queryKey: fetchQuotesKey(params.clientId),
    queryFn: () => fetchQuotes(params, { ...customOptions }),
    ...queryOptions,
  });
}
